<template>
  <form class="edit_profile" @submit.prevent="saveProfile">
    <div
      class="edit_profile__description"
      :style="{
        background: bgColor?.colors
          ? `linear-gradient(225deg, ${bgColor?.colors[0] || '#000000'} 50%, ${bgColor?.colors[1] || '#000000'} 50%)`
          : 'transparent',
      }"
    >
      <div class="edit_profile__description_bg"></div>

      <div class="edit_profile__description_header">
        <button
          class="edit_profile__exit"
          type="button"
          aria-label="Back"
          @click="$router.push('/profile')"
        >
          <img src="/arrow.svg" loading="lazy" />
        </button>
        <p>{{ $t("profileEdit.title") }}</p>
      </div>

      <div class="edit_profile__form">
        <div class="edit_profile__form_main">
          <AvatarPicker :user="user" v-model:avatar="avatar" />

          <div class="edit_profile__form_text">
            <div class="edit_profile__form_input">
              <input
                v-model="name"
                type="text"
                :placeholder="$t('profileEdit.formPlaceholder.name')"
                id="name"
                name="name"
              />
              <button class="edit_profile__form_btn" type="button">
                <img src="/highlighter.svg" loading="lazy" />
              </button>
            </div>

            <div class="edit_profile__form_input">
              <input
                type="text"
                :placeholder="$t('profileEdit.formPlaceholder.nickname')"
                v-model="username"
                id="username"
                name="username"
              />
              <button class="edit_profile__form_btn" type="button">
                <img src="/highlighter.svg" loading="lazy" />
              </button>
            </div>
          </div>

          <div class="edit_profile__fon">
            <ColorPicker :bgColor="bgColor" @update-color="bgColor = $event" />
          </div>
        </div>

        <BubbleInput
          :about_me="about_me"
          v-model:about_me="about_me"
          :type="selectedBubble"
        />
      </div>

      <div class="bubble_form">
        <p>{{ $t("profileEdit.bubbleType") }}</p>

        <div class="bubble_form__choises">
          <div
            v-for="(bubble, index) in bubbleOptions"
            :key="index"
            class="bubble_form__choise"
            :class="{ active: selectedBubble === bubble.value }"
            @click="selectBubble(bubble.value)"
          >
            <img :src="bubble.src" alt="Bubble" loading="lazy" />
          </div>
        </div>
      </div>
    </div>

    <div class="edit_profile__tegs">
      <div class="edit_profile__tegs_header">
        <h4>{{ $t("profileEdit.settings.title") }}</h4>
        <p>{{ $t("profileEdit.settings.description") }}</p>

        <p class="edit_profile__tegs_error" v-if="errorSummary">
          {{ $t("profileEdit.settings.error") }} {{ errorSummary }}
        </p>
      </div>

      <div class="edit_profile__tegs_form">
        <EditInput
          :error="errors?.phonenumber"
          :name="$t('profileEdit.settings.inputs.number.label')"
          type="phone"
          :placeholder="$t('profileEdit.settings.inputs.number.placeholder')"
          v-model="phone"
        />
        <EditInput
          :error="errors?.date"
          :name="$t('profileEdit.settings.inputs.date.label')"
          type="date"
          :placeholder="$t('profileEdit.settings.inputs.date.placeholder')"
          v-model="birthday"
        />
        <EditInput
          :error="errors?.email"
          :name="$t('profileEdit.settings.inputs.email.label')"
          type="email"
          :placeholder="$t('profileEdit.settings.inputs.email.placeholder')"
          v-model="email"
        />
        <EditInput
          :error="errors?.city"
          :name="$t('profileEdit.settings.inputs.city.label')"
          :placeholder="$t('profileEdit.settings.inputs.city.placeholder')"
          v-model="location"
        />
        <EditInput
          :error="errors?.link"
          :name="$t('profileEdit.settings.inputs.social.label')"
          type="link"
          :placeholder="$t('profileEdit.settings.inputs.social.placeholder')"
          v-model="link"
        />

        <label class="edit_input">
          {{ $t("profileEdit.settings.inputs.region.label") }}
          <MySelect
            v-model="language_zone"
            :list="langStore.languages"
            valueKey="code"
            labelKey="label"
          />
        </label>
      </div>

      <div class="edit_profile__tegs_bottom">
        <WhiteButton :loading="loading">
          {{ $t("profileEdit.button") }}
        </WhiteButton>
      </div>
    </div>
  </form>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
import { useAuthStore } from "@/stores/authStore";
import AvatarPicker from "./AvatarPicker.vue";
import ColorPicker from "./ColorPicker.vue";
import BubbleInput from "./BubbleInput.vue";
import { putProfile } from "@/api/axios.js";
import { useRouter } from "vue-router";
import { parseISO, formatISO } from "date-fns";
import EditInput from "@/UI/Input/EditInput.vue";
import MySelect from "@/UI/MySelect.vue";
import { useLanguageStore } from "@/stores/languageStore";
import WhiteButton from "@/UI/Button/WhiteButton.vue";

const langStore = useLanguageStore();

const bubbleOptions = [
  { value: "simpleBubbleFill", src: "/bubble/simpleBubbleFill.svg" },
  { value: "dottedBubble", src: "/bubble/dottedBubble.svg" },
  { value: "rectangleBubble", src: "/bubble/rectangleBubble.png" },
  { value: "agessiveBubble", src: "/bubble/agessiveBubble.svg" },
  { value: "pixelBubble", src: "/bubble/pixelBubble.png" },
  { value: "cloudBubble", src: "/bubble/cloudBubble.svg" },
  { value: "kindBubble", src: "/bubble/kindBubble.svg" },
  { value: "barbedBubble", src: "/bubble/barbedBubble.svg" },
  { value: "simpleBubble", src: "/bubble/simpleBubble.svg" },
];

const auth = useAuthStore();
const router = useRouter();
const user = ref(null);
const errors = ref({});

const bgColor = ref({ colors: ["#000000", "#000000"] });
const about_me = ref("");
const name = ref("");
const username = ref("");
const avatar = ref("");

const phone = ref("");
const link = ref("");
const location = ref("");
const birthdayString = ref("");
const email = ref("");
const language_zone = ref("");
const loading = ref(false);

const birthday = computed({
  get() {
    return birthdayString.value ? parseISO(birthdayString.value) : null;
  },
  set(val) {
    birthdayString.value = val
      ? formatISO(val, { representation: "date" })
      : null;
  },
});

onMounted(async () => {
  langStore.loadLanguages();

  if (auth.token && !auth.user) {
    try {
      await auth.fetchUser();
      user.value = auth.user;
    } catch (e) {
      console.error("Ошибка при загрузке пользователя:", e);
    }
  } else {
    user.value = auth.user;
  }

  if (user.value) {
    name.value = user.value.name;
    username.value = user.value.username;
    phone.value = user.value.phonenumber ?? "";
    about_me.value = user.value.about_me ?? "";
    location.value = user.value.location ?? "";
    email.value = user.value.email ?? "";
    link.value = Array.isArray(user.value.links)
      ? (user.value.links[0] ?? "")
      : "";

    birthdayString.value = user.value.birthday
      ? user.value.birthday.split("T")[0]
      : "";

    const firstColor = user.value.metadata?.firstBackground_color || "#000000";
    const secondColor =
      user.value.metadata?.secondBackground_color || "#000000";

    bgColor.value = {
      colors: [firstColor, secondColor],
    };

    language_zone.value = user.value.language_zone;
  }
});

const selectedBubble = ref(
  auth.user?.metadata?.bubble_type || "simpleBubbleFill",
);

const selectBubble = (bubbleType) => {
  selectedBubble.value = bubbleType;
};

const saveProfile = async () => {
  errors.value = {};
  loading.value = true;
  const bgColors = bgColor.value?.colors || ["#000000", "#000000"];

  const payload = {
    name: name.value,
    username: username.value,
    email: email.value,
    language_zone: language_zone.value,
    metadata: {
      main_text_color: "#ffffff",
      bubble_type: selectedBubble.value,
      firstBackground_color: bgColors[0],
      secondBackground_color: bgColors[1],
    },
    about_me: about_me.value,
    phonenumber: phone.value,
    links: [link.value],
    location: location.value,
    birthday: birthdayString.value
      ? new Date(birthdayString.value).toISOString()
      : null,
  };

  if (avatar.value) {
    payload.userpic = { url: avatar.value };
  }

  try {
    const res = await putProfile(payload);

    if (res.status === 200) {
      await auth.fetchUser();
      router.push({ name: "Profile" });
    }
  } catch (err) {
    if (err.response?.data) {
      errors.value = err.response.data;
    } else {
      console.error("Error when saving the profile:", err);
    }
  } finally {
    loading.value = false;
  }
};

const errorSummary = computed(() => {
  const formFields = ["email", "phonenumber", "date", "link", "city"];

  if (!errors.value) return "";

  return Object.keys(errors.value)
    .filter((key) => formFields.includes(key))
    .join(", ");
});
</script>

<style>
.edit_profile__description {
  width: 100%;
  border-radius: 20px;
  margin-bottom: 20px;
  position: relative;
  background: transparent;
}

.edit_profile__description_bg {
  position: absolute;
  width: 100%;
  height: 100%;
  backdrop-filter: blur(200px);
  z-index: 0;
  border-radius: 20px;
}

.edit_profile__description::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  border-radius: 20px;
  height: 100%;
  z-index: 0;
  background: linear-gradient(
    86deg,
    #181818 0%,
    rgba(24, 24, 24, 0) 55.07%,
    #181818 100%
  );
}

.edit_profile__tegs {
  width: 100%;
  background: var(--edit-profile-form);
  border-radius: 20px;
  margin-bottom: 20px;
}

.edit_profile__description_header {
  padding: 10px;
  display: flex;
  text-align: center;
  color: white;
  background: var(--edit-profile-color);
  z-index: 2;
  font-size: 14px;
  box-sizing: border-box;
  position: relative;
  border-radius: 20px 20px 0 0;
}

.edit_profile__description_header p {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.edit_profile__exit {
  padding: 10px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 15px;
  cursor: pointer;
  transition: background 0.2s ease;
}

.edit_profile__exit:hover {
  background: rgba(255, 255, 255, 0.2);
}

.edit_profile__exit img {
  transform: rotate(90deg);
}

.edit_profile__form_main {
  display: flex;
}

.edit_profile__form {
  gap: 10px;
  padding: 16px;
  position: relative;
  z-index: 2;
}

.edit_profile__form_text {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 10px;
  flex: 1;
  margin-left: 14px;
}

.edit_profile__fon {
  position: relative;
}

.edit_profile__form_fon {
  background-color: rgba(255, 255, 255, 0.1);
  padding: 12px 16px;
}

.edit_profile__form_fon {
  color: white;
  height: 44px;
  border-radius: 15px;
  cursor: pointer;
  display: flex;
  align-items: center;
  transition: background 0.2s ease;
}

.edit_profile__form_fon img {
  margin-right: 8px;
}

.edit_profile__form_fon:hover {
  background: rgba(255, 255, 255, 0.2);
}

.edit_profile__form_avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.1);
  width: 98px;
  height: 98px;
  box-sizing: border-box;
  border-radius: 10px;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.edit_profile__form_avatar:hover {
  background-color: rgba(0, 0, 0, 0.2);
}

.bubble_form {
  background: var(--edit-profile-color);
  padding: 16px;
  position: relative;
  border-bottom-left-radius: 20px;
  border-bottom-right-radius: 20px;
  color: white;
}

.edit_profile__form_input {
  display: flex;
}

.edit_profile__form_input input {
  max-width: 200px;
  background-color: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  padding: 6px 12px;
  border-top-left-radius: 10px;
  border-bottom-left-radius: 10px;
  color: white;
  box-sizing: border-box;
}

.edit_profile__form_input input:focus {
  border: none;
}

.edit_profile__form_btn {
  padding-right: 8px;
  padding-left: 4px;
  border-top-right-radius: 15px;
  border-bottom-right-radius: 15px;
  background-color: rgba(255, 255, 255, 0.3);
}

.edit_profile__form_btn img {
  width: 16px;
  height: 16px;
}

.edit_profile__tegs_header {
  padding: 16px 0 0 16px;
  margin-bottom: 24px;
}

.edit_profile__tegs_header p {
  color: #7d7d7d;
}

.edit_profile__tegs_form {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  width: 100%;
  padding: 0 16px 24px;
  margin-bottom: 10px;
  box-sizing: border-box;
}

.edit_profile__tegs_bottom {
  background: var(--edit-profile-form);
  padding: 16px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  border-bottom-left-radius: 15px;
  border-bottom-right-radius: 15px;
}

.bubble_form__choises {
  display: flex;
  margin-top: 15px;
  gap: 24px;
  flex-wrap: wrap;
}

.bubble_form__choise {
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  padding-left: 25px;
  padding-top: 5px;
  width: 67px;
  height: 67px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  box-sizing: border-box;
  position: relative;
  transition: background-color 0.2s ease;
}

.bubble_form__choise img {
  width: 47px;
  height: 60px;
}

.bubble_form__choise.active::before {
  content: "";
  position: absolute;
  top: -8px;
  left: -7px;
  right: 0;
  bottom: 0;
  border: 4px solid white;
  width: 110%;
  height: 110%;
  border-radius: 16px;
}

.bubble_form__choise.active img {
  transform: translateY(-3px);
}

.edit_profile__tegs_error {
  color: #c44 !important;
}

.bubble_form__choise:hover {
  background-color: rgba(255, 255, 255, 0.2);
}

.edit_profile__form_avatar.avatar {
  width: 100%;
  height: 100%;
}

@media (max-width: 920px) {
  .edit_input {
    text-align: left;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-end;
  }

  .edit_input input {
    box-sizing: border-box;
  }

  .edit_profile__tegs_form {
    grid-template-columns: 1fr;
  }

  .edit_input input,
  .custom_select {
    width: 50% !important;
  }

  .v3dp__input_wrapper,
  .v3dp__datepicker {
    width: 100%;
  }

  .edit_profile__tegs {
    margin-bottom: 50px;
  }
}

@media (max-width: 560px) {
  .edit_profile__tegs_form {
    padding: 0 10px 16px;
    grid-template-columns: 1fr;
  }

  .edit_input {
    font-size: 0.85rem;
  }

  .edit_input input,
  .custom_select {
    padding: 0.7rem 0.9rem;
    font-size: 0.9rem;
    width: 100% !important;
  }

  .edit_profile__submit {
    width: 100%;
    padding: 10px;
  }

  .edit_profile__form_main {
    flex-direction: column-reverse;
    gap: 16px;
  }

  .edit_profile__form_text {
    margin-left: 0;
    width: 100%;
  }

  .edit_profile__form_input input {
    max-width: 100% !important;
    width: 100% !important;
  }

  .edit_profile__fon {
    align-self: flex-end;
  }
}

@media (max-width: 400px) {
  .edit_profile {
    padding: 0 8px;
  }

  .edit_profile__tegs_form {
    padding: 0 8px 12px;
  }

  .edit_input input,
  .custom_select {
    padding: 0.6rem 0.8rem;
    font-size: 0.85rem;
    width: 100%;
  }
}
</style>
